import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { ConfirmationModal, MenuItem, Select, Spinner, Typography } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './RemoveFromTeamModal.module.scss';
const commonModalProps = {
    title: 'Remove team member',
    skin: 'danger',
    primaryButtonLabel: 'Remove team member',
    secondaryButtonLabel: 'Cancel'
};
export default function RemoveFromTeamModal(props) {
    const { member, dismissModal } = props;
    const { api } = useStoreActions();
    const onClose = ()=>dismissModal();
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>api.member.getAssignedRelationships(member.id()),
        renderChild: (param)=>{
            let { data } = param;
            return /*#__PURE__*/ _jsx(RemoveFromTeamModalWithData, _object_spread_props(_object_spread({}, props), {
                numAssignedRelationships: data
            }));
        },
        renderLoader: ()=>/*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, commonModalProps), {
                content: /*#__PURE__*/ _jsx(Spinner, {}),
                primaryButtonDisabled: true,
                onCloseButtonClick: onClose,
                onSecondaryButtonClick: onClose
            }))
    });
}
function RemoveFromTeamModalWithData(props) {
    const { member, allMembers, numAssignedRelationships, onRefresh, dismissModal } = props;
    const isVendor = useStore(selectors.user.isVendor);
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            memberIdToReassign: ''
        }
    });
    const hasAssignedRelationships = !!numAssignedRelationships;
    const onClose = ()=>dismissModal();
    const optionsToReassign = useMemo(()=>{
        return allMembers.filter((m)=>m.id() !== member.id() && m.isAssignable(isVendor)).map((m)=>({
                id: m.id(),
                text: m.fullName()
            }));
    }, [
        allMembers,
        member,
        isVendor
    ]);
    const onRemove = async (param)=>{
        let { memberIdToReassign } = param;
        const onRemoveLoader = 'rmMember';
        overlayContext.startLoader(onRemoveLoader);
        try {
            if (hasAssignedRelationships) {
                await api.member.reassignAllRelationships({
                    assignedUser: member.id(),
                    assignToUser: memberIdToReassign
                });
            }
            await api.member.remove({
                id: member.id(),
                currentRole: member.effectiveRole()
            });
            logBI(12203, {
                id: member.id(),
                email: member.email(),
                reassignTo: memberIdToReassign
            });
        } catch (err) {
            overlayContext.showErrorToast('Failed to remove team member', 'rm-member-fail');
            return overlayContext.stopLoader(onRemoveLoader);
        }
        try {
            await onRefresh();
        } finally{
            overlayContext.stopLoader(onRemoveLoader);
            dismissModal();
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, commonModalProps), {
        testId: "rm-member",
        textAlignCenter: false,
        onCloseButtonClick: onClose,
        onSecondaryButtonClick: onClose,
        onPrimaryButtonClick: handleSubmit(onRemove),
        bi: {
            primaryButton: {
                name: 'remove_team_member'
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        },
        content: hasAssignedRelationships ? /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    className: styles.paragraph,
                    children: [
                        "In order to remove",
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            as: "span",
                            weight: "normal",
                            children: member.fullName()
                        }),
                        ", you need to select another team member to reassign all proposals and agreements under their name."
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    className: styles.paragraph,
                    children: "Please select one from the list"
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    name: "memberIdToReassign",
                    control: control,
                    rules: requiredValidation(),
                    render: (p)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                            collapseHelperText: false,
                            className: styles.paragraph,
                            width: 280,
                            testId: "rm-member-reassign",
                            label: "Select team member",
                            bi: {
                                name: 'select_team_member'
                            }
                        }, connectControllerToTextField(p)), {
                            children: optionsToReassign.map((param)=>{
                                let { id, text } = param;
                                return /*#__PURE__*/ _jsx(MenuItem, {
                                    value: id,
                                    testId: "rm-member-reassign-opt-".concat(id),
                                    children: text
                                }, id);
                            })
                        }))
                })
            ]
        }) : /*#__PURE__*/ _jsxs(Typography, {
            children: [
                "Are you sure you want to remove",
                ' ',
                /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    weight: "normal",
                    children: member.fullName()
                }),
                ' ',
                "from your team? Once removed they will no longer be able to access your business."
            ]
        })
    }));
}
